import React, { useState, useEffect } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { dracula } from "@uiw/codemirror-theme-dracula";
import { abcdef } from "@uiw/codemirror-theme-abcdef";
import { androidstudio } from "@uiw/codemirror-theme-androidstudio";
import { sublime } from "@uiw/codemirror-theme-sublime";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Button, Spinner, Tooltip } from "flowbite-react";
import GuardedActionButton from "./GuardedActionButton.jsx";
import ButtonLoader from "./ButtonLoader.jsx";
import { EditorView } from "@codemirror/view";
import {
  useSaveAssignmentCodeMutation,
  useSaveAutoSaveMutation,
  useModuleAutoSaveMutation,
} from "../../../graphql/generated.tsx";
import ConformationModal from "../../Admin/Components/ConformationModal.jsx";
// Removed toast usage for duplicate save notifications per requirement

const COPY_PASTE_ALLOWED_STUDENT_IDS = new Set([
  "6fe06698-0282-4d68-8d69-4372f5d69f81",
  "43265580-acfa-44ed-9789-b780ff98164b",
  "5bceb9a4-9d25-438f-940f-eadf8dffb990",
  "4635e33a-55b2-42eb-a78c-6cd154a9be61",
  "2997a790-1422-426e-ba0d-79cad583e803",
  "13439677-7451-4a20-b23d-3a326e1bf5e2",
  "c4458b04-1c84-423c-9cdd-07166967f902",
  "d024566d-dfed-47f1-afc1-d588b1702cd0",
  "6baeb136-deae-4ab9-af04-6bd17ba288b4",
  "1714bb8a-b8ec-4d3b-913d-4860612f6047",
  "7e5e7fd3-2570-40b2-88cc-4dcedd77855c",
  "eec6727f-1c96-4ba1-b903-1873f9be682b",
  "c56e374b-9d96-4455-8dc7-b6ad0299003f",
  "9bf45ebc-b2ee-4b1f-b393-3da6c1aae91f",
  "8e77ee33-db94-4520-9172-afdb92635cef",
  "8e01dc34-813e-46ab-ab14-191378d42cd2",
]);

const Compiler = ({
  language,
  isAssignment,
  isModule,
  moduleAutoSaveInput, // new prop for module save input
  submitInput,
  setLanguage,
  setToDefault,
  handleCodeChange,
  errorRef,
  handleSubmit,
  code,
  handleRun,
  CourseData,
  compileLoading,
  submitLoading,
  question_id,
  student_id,
  previewMode,
  isChecked,
  setCodeApplied,
  isCodeChanged,
  setInitialCode,
  disabledReset, // Add disabledReset prop
  defaultCode,
  onCustomSave, // Custom save handler
  manualInput, // new optional prop for manual custom test case input (only relevant when isChecked true)
}) => {
  const themes = [
    { label: "Dracula", value: dracula },
    { label: "ABCDEF", value: abcdef },
    { label: "Android Studio", value: androidstudio },
    { label: "Sublime", value: sublime },
  ];

  const [autoSave, { loading }] = useSaveAutoSaveMutation();
  const [autoSaveAssignments] = useSaveAssignmentCodeMutation();
  const [selectedTheme, setSelectedTheme] = useState(dracula);
  const [languageExtensions, setLanguageExtensions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [disableCopyPaste, setDisableCopyPaste] = useState([]);
  const [lastRunCode, setLastRunCode] = useState("");
  const [hasCodeChanged, setHasCodeChanged] = useState(true);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [isSwitchingLanguage, setIsSwitchingLanguage] = useState(false);
  const [autoSaveOnRunAndSubmit, setAutoSaveOnRunAndSubmit] = useState(false);
  const [moduleAutoSave] = useModuleAutoSaveMutation();
  const [lastRunManualInput, setLastRunManualInput] = useState("");
  const [runBurst, setRunBurst] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [newLanguage, setNewLanguage] = useState(language); // Keep track of new language

  useEffect(() => {
    const loadLanguageExtensions = async () => {
      try {
        let extensions = [];
        switch (language) {
          case "CPP":
          case "C":
            {
              const { cpp } = await import("@codemirror/lang-cpp");
              extensions.push(cpp());
            }
            break;
          case "PYTHON":
            {
              const { python } = await import("@codemirror/lang-python");
              extensions.push(python());
            }
            break;
          case "JAVA":
            {
              const { java } = await import("@codemirror/lang-java");
              extensions.push(java());
            }
            break;
          default:
            break;
        }
        setLanguageExtensions(extensions);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Language load error:", e);
        setLanguageExtensions([]);
      }
    };
    loadLanguageExtensions();
  }, [language]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!previewMode && isCodeChanged) {
        handleSaveCode(); // Save the code before the user leaves
      }
      event.preventDefault();
      event.returnValue = "";
    };

    if (!previewMode && isCodeChanged) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [code, language, previewMode, isCodeChanged]);

  useEffect(() => {
    if (!previewMode && !isSaving && isCodeChanged) {
      const interval = setInterval(() => {
        handleSaveCode();
      }, 1000 * 60 * 2); // Auto-save every 1 minute

      return () => clearInterval(interval);
    }
  }, [
    autoSave,
    question_id,
    student_id,
    code,
    language,
    previewMode,
    isSaving,
    isCodeChanged,
  ]);

  const handleSaveCode = async () => {
    if (autoSaveOnRunAndSubmit) {
      setAutoSaveOnRunAndSubmit(false);
      return;
    }

    if (!isCodeChanged) {
      return; // silently ignore when no changes
    }
    if (isSaving || isSwitchingLanguage) return Promise.resolve();
    setSaveStatus("saving");
    setIsSaving(true);

    try {
      if (isModule) {
        // Use moduleAutoSave mutation for module screens
        await moduleAutoSave({
          variables: moduleAutoSaveInput,
        });
      } else if (isAssignment) {
        await autoSaveAssignments({
          variables: {
            saveCodeInput: submitInput,
            code,
            language,
          },
        });
      } else {
        await autoSave({
          variables: {
            submitInput: submitInput,
            question_id,
            student_id,
            code,
            language,
          },
        });
      }

      setSaveStatus("saved");
      setTimeout(() => {
        setSaveStatus("idle");
      }, 2000);
      if (setInitialCode) {
        setInitialCode(code);
      }
    } catch (error) {
      console.error("Save code error:", error);
      setSaveStatus("idle");
    } finally {
      setIsSaving(false);
    }
  };

  // Track whether run button should be enabled.
  // Normal mode (isChecked false): enable only if code changed vs lastRunCode.
  // Manual mode (isChecked true): enable only if code OR manualInput changed vs previous manual run snapshot.
  useEffect(() => {
    if (isChecked) {
      if (code !== lastRunCode || manualInput !== lastRunManualInput) {
        setHasCodeChanged(true);
      } else {
        setHasCodeChanged(false);
      }
    } else {
      if (code !== lastRunCode) {
        setHasCodeChanged(true);
      } else {
        setHasCodeChanged(false);
      }
    }
  }, [code, lastRunCode, isChecked, manualInput, lastRunManualInput]);

  useEffect(() => {
    setAutoSaveOnRunAndSubmit(true);
  }, []);

  const handleRunClick = async () => {
    // Only proceed if something actually changed per computed flag
    if (!hasCodeChanged) return;
    try {
      await Promise.resolve(handleRun());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("RUN error:", e);
    }
    // trigger immediate visual burst so effect appears even before compileLoading flips
    setRunBurst(true);
    setTimeout(() => setRunBurst(false), 900);
    handleRun();
    setLastRunCode(code);
    if (isChecked) {
      setLastRunManualInput(manualInput || "");
    }
    setHasCodeChanged(false);
    setAutoSaveOnRunAndSubmit(true);
    try {
      await handleSaveCode();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Auto-save after RUN error:", e);
    }
    setSaveStatus("saved");
    setTimeout(() => setSaveStatus("idle"), 10000);
  };

  const handleLanguageChange = (e) => {
    const selectedLanguage = e.target.value;
    setNewLanguage(selectedLanguage);
    setCodeApplied(false);
    if (code && code.trim() !== "" && !previewMode && code !== defaultCode) {
      setOpenModal(true);
    } else {
      setLanguage(selectedLanguage);
    }
  };

  const handleConfirmLanguageChange = () => {
    setCodeApplied(false);
    setIsSwitchingLanguage(true);
    handleSaveCode().then(() => {
      setLanguage(newLanguage);
      setIsSwitchingLanguage(false);
      setOpenModal(false); // Close modal after confirming
    });
  };
  const handleSubmitAndMarkSaved = async () => {
    setAutoSaveOnRunAndSubmit(true);
    try {
      await handleSubmit();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("SUBMIT error:", e);
    }
    setSaveStatus("saved");
    setTimeout(() => setSaveStatus("idle"), 10000); // Reset if needed
  };

  useEffect(() => {
    if (COPY_PASTE_ALLOWED_STUDENT_IDS.has(student_id)) {
      setDisableCopyPaste([]);
    } else {
      const handlers = EditorView.domEventHandlers({
        keydown: (event) => {
          if (
            event.ctrlKey &&
            (event.key === "c" || event.key === "v" || event.key === "x")
          ) {
            event.preventDefault();
          }
        },
        copy: (event) => event.preventDefault(),
        paste: (event) => event.preventDefault(),
        cut: (event) => event.preventDefault(),
        drop: (event) => event.preventDefault(),
        dragover: (event) => event.preventDefault(),
      });
      setDisableCopyPaste([handlers]);
    }
  }, [student_id]);

  return (
    <>
      <ConformationModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        event={handleConfirmLanguageChange}
        text="Your progress may be lost. Do you want to switch the language?"
        sidebarMode="warning"
        primaryButtonText="Continue"
        heading="Switch Language"
        subtitle="You have unsaved changes in your current code."
        confirmTitle="Switch language"
      />

      <div className="flex items-center justify-between w-full px-5 py-2 bg-white rounded-t-lg gap-x-4">
        <FormControl className="w-[130px]">
          <InputLabel id="language-select-label">Language</InputLabel>
          <Select
            labelId="language-select-label"
            value={language}
            label="Language"
            disabled={isSaving || saveStatus === "saving"}
            onChange={handleLanguageChange} // Trigger the modal on change
          >
            {CourseData?.languages?.map((lang) => (
              <MenuItem key={lang} value={lang.toUpperCase()}>
                {lang}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className="w-[200px] max-md:w-[100px]">
          <InputLabel id="theme-select-label">Theme</InputLabel>
          <Select
            labelId="theme-select-label"
            value={selectedTheme}
            label="Theme"
            onChange={(e) => setSelectedTheme(e.target.value)}
          >
            {themes.map((theme) => (
              <MenuItem key={theme.label} value={theme.value}>
                {theme.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!previewMode && (
          <>
            <Button
              onClick={setToDefault}
              className="text-white rounded-md bg-primary max-md:p-0 max-md:text-sm"
              disabled={disabledReset} // Disable button when code is at default/reset state
            >
              Reset Code
            </Button>
            {/* Save Button with Tooltip only when disabled and not after save */}
            {!isCodeChanged && saveStatus === "idle" ? (
              <Tooltip content="No changes in current code to save." placement="top">
                <span>
                  <Button
                    className={`bg-primary text-white rounded-md max-md:p-0 max-md:text-sm transition-all duration-300 ease-in-out`}
                    onClick={handleSaveCode}
                    disabled
                  >
                    Save Code
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                className={`bg-primary text-white rounded-md max-md:p-0 max-md:text-sm transition-all duration-300 ease-in-out ${saveStatus === "saved" ? "" : "bg-primary"
                  }`}
                onClick={() => {
                  handleSaveCode();
                  if (onCustomSave) {
                    onCustomSave();
                  }
                }}
                disabled={!isCodeChanged || saveStatus === "saving"}
              >
                {saveStatus === "saving" && (
                  <>
                    <Spinner
                      aria-label="Medium sized spinner example"
                      size="sm"
                      className="mr-2"
                    />
                    Saving...
                  </>
                )}
                {saveStatus === "saved" && (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5 mr-2 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Saved
                  </>
                )}
                {saveStatus === "idle" && "Save Code"}
              </Button>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col">
        <div className="w-full">
          <CodeMirror
            value={code}
            height="600px"
            width="100%"
            theme={selectedTheme}
            extensions={[...languageExtensions, ...disableCopyPaste]}
            options={{
              mode: "text/x-csrc",
              lineNumbers: true,
              readOnly: "nocursor",
            }}
            onChange={handleCodeChange}
          />
        </div>
      </div>

      <div ref={errorRef} className="flex "></div>
      {!previewMode && (
        <div className="flex justify-end gap-2 mt-4">
          {(!hasCodeChanged) ? (
            <Tooltip content={isChecked ? "No changes in code or test case" : "Edit your code to enable"}>
              <span>
                <Button
                  className="text-white bg-blue-500 rounded-md shadow-md hover:bg-blue-600 max-md:p-0 max-md:text-sm"
                  onClick={handleRunClick}
                  disabled
                >
                  <ButtonLoader compiling={compileLoading || runBurst} labelIdle="RUN" />
                </Button>
              </span>
            </Tooltip>
          ) : (
            <GuardedActionButton
              className="text-white bg-blue-500 rounded-md shadow-md hover:bg-blue-600 max-md:p-0 max-md:text-sm"
              onAction={handleRunClick}
              disabled={compileLoading}
              loading={false}
              spinnerOnChecking={false}
              label={<ButtonLoader compiling={compileLoading || runBurst} labelIdle="RUN" compilingLabel="Compiling" />}
            />
          )}

          <GuardedActionButton
            className="text-white bg-green-600 rounded-md shadow-md hover:bg-green-700 max-md:p-0 max-md:text-sm"
            onAction={handleSubmitAndMarkSaved}
            loading={false}
            spinnerOnChecking={false}
            label={<ButtonLoader compiling={submitLoading} labelIdle="SUBMIT" compilingLabel="Processing" />}
          />
        </div>
      )}
      
    </>
  );
};

export default Compiler;